import React, { useEffect, useState } from "react";
import { Route, useRouteMatch, Link, Switch, Redirect, useLocation } from "react-router-dom";
import SyncCoreplus from "./SyncCoreplus";
import TrackPatients from "./TrackPatients";
import PractitionerSchedule from "./PractitionerSchedule";
import PractitionerEmailReviewingConfig from "./PractitionerEmailReviewingConfig";
import PractitionerEmailNotificationConfig from "./PractitionerEmailNotificationConfig";
import PostcodeUpload from "./PostcodeUpload";
import SMSTemplatesPanel from "./SMSTemplatesPanel";
import EmailTemplates from "./EmailTemplates";
import ClinicCommunicationMethod from "./ClinicCommunicationMethod";
import InteractiveVoiceResponse from "./InteractiveVoiceResponse";
import EmailNotificationQueue from "./EmailNotificationQueue";
import { isExperimentsEnabled } from "../../infra/featureToggle";
import RelationshipPanel from "./RelationshipPanel";
import Report from "./Report";
import PractitionerLocation from "./PractitionerLocation";
import Coordinators from "./Coordinators";
import PractitionerProfile from "./PractitionerProfile";
import ManualSmsTemplates from "./ManualSmsTemplates";
import TelecareEditorPage from "./TelecareEditorPage";
import PractitionerPermissions from "./PractitionerPermissions";
import { useSelector } from "react-redux";
import { SCOPE } from "../../constants";
import { isPermit } from "../../utils";
import SpecialityEditor from "./SpecialityEditor";
import CreateDemoReferral from "./demoReferralForDev/CreateDemoReferral";
import SecureFileRetentionSettings from "./SecureFileRetentionSettings";
import { getToggleEnabled, TOGGLES } from "../../toggles";

const Settings = () => {
  const isSecureFilesEnabled = getToggleEnabled(TOGGLES.SECURE_FILES);
  const { pathname } = useLocation();
  const { path, url } = useRouteMatch();
  const [menuName, setMenuName] = useState("track_patients");
  const currentUser = useSelector((state) => state.user?.currentUser);
  const dataFromPMS = process.env.REACT_APP_DATA_SOURCE === "pms-api";
  useEffect(() => {
    if (pathname) {
      const [, , , path] = pathname.split("/");
      if (!path) {
        return;
      }
      setMenuName(path.split("-").join("_"));
    }
  }, [pathname]);

  return (
    <>
      <div className="columns is-mobile">
        <div className="column is-one-quarter has-background-info-light">
          <aside className="menu">
            <p className="menu-label">Client</p>
            <ul className="menu-list is-size-7">
              <li>
                <Link
                  to={`${url}/track-patients`}
                  className={menuName === "track_patients" ? "is-active is-radiusless" : ""}
                  onClick={() => setMenuName("track_patients")}
                >
                  Track Patients
                </Link>
              </li>
            </ul>
            {isPermit(currentUser?.role, SCOPE.OTHER_SETTINGS) && (
              <>
                <p className="menu-label">Practitioner</p>
                <ul className="menu-list is-size-7">
                  <li>
                    <Link
                      to={`${url}/clinician-preference`}
                      className={menuName === "clinician_preference" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("clinician_preference")}
                    >
                      Clinician Preference
                    </Link>
                  </li>
                  <li>
                    <a href="#abc">Notifications</a>
                    <ul>
                      <li>
                        <Link
                          to={`${url}/referral-triage-notification`}
                          className={menuName === "referral_triage_notification" ? "is-active is-radiusless" : ""}
                          onClick={() => setMenuName("referral_triage_notification")}
                        >
                          Referral Triage Notification
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${url}/appointment-booking-notification`}
                          className={menuName === "appointment_booking_notification" ? "is-active" : ""}
                          onClick={() => setMenuName("appointment_booking_notification")}
                        >
                          Appointment Booking Notification
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${url}/patient-portal-booking-toggle`}
                          className={menuName === "patient_portal_booking_toggle" ? "is-active" : ""}
                          onClick={() => setMenuName("patient_portal_booking_toggle")}
                        >
                          Patient Portal Booking Toggle
                        </Link>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <Link
                      to={`${url}/practitioner-location`}
                      className={menuName === "practitioner_location" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("practitioner_location")}
                    >
                      Setup Practitioner Location
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/speciality`}
                      className={menuName === "speciality" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("speciality")}
                    >
                      Speciality
                    </Link>
                  </li>
                </ul>
                <p className="menu-label">Admin</p>
                <ul className="menu-list is-size-7">
                  <li>
                    <Link
                      to={`${url}/sync-coreplus`}
                      className={menuName === "sync_coreplus" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("sync_coreplus")}
                    >
                      {dataFromPMS ? "Synchronize with PMS" : "Synchronize with Coreplus"}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/postcode-timezone-mapping`}
                      className={menuName === "postcode_timezone_mapping" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("postcode_timezone_mapping")}
                    >
                      Postcode Timezone Mapping
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/sms-templates`}
                      className={menuName === "sms_templates" ? "is-active" : ""}
                      onClick={() => setMenuName("sms_templates")}
                    >
                      Auto SMS Templates
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/manual-sms-templates`}
                      className={menuName === "manual_sms_templates" ? "is-active" : ""}
                      onClick={() => setMenuName("manual_sms_templates")}
                    >
                      Manual SMS Templates
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/email-templates`}
                      className={menuName === "email_templates" ? "is-active" : ""}
                      onClick={() => setMenuName("email_templates")}
                    >
                      Email Templates
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/referrer-communication-preference`}
                      className={menuName === "referrer_communication_preference" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("referrer_communication_preference")}
                    >
                      Referrer Communication Preference
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/email-notification-queue`}
                      className={menuName === "email_notification_queue" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("email_notification_queue")}
                    >
                      Email Notification Queue
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/assign-clinician-to-staff`}
                      className={menuName === "assign_clinician_to_staff" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("assign_clinician_to_staff")}
                    >
                      Assign Clinician to Staff
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/report`}
                      className={menuName === "report" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("report")}
                    >
                      Report
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/account-setting`}
                      className={menuName === "account_setting" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("account_setting")}
                    >
                      Account Setting
                    </Link>
                  </li>
                  <li>
                    <Link
                      to={`${url}/practitioner-profile`}
                      className={menuName === "practitioner_profile" ? "is-active is-radiusless" : ""}
                      onClick={() => setMenuName("practitioner_profile")}
                    >
                      Practitioner profile
                    </Link>
                  </li>
                  {isSecureFilesEnabled && (
                    <li>
                      <Link
                        to={`${url}/secure-file-retention`}
                        className={menuName === "secure_file_retention" ? "is-active is-radiusless" : ""}
                        onClick={() => setMenuName("secure_file_retention")}
                      >
                        Secure File Retention
                      </Link>
                    </li>
                  )}
                </ul>
                {isExperimentsEnabled && (
                  <>
                    <p className="menu-label">Experiments</p>
                    <ul className="menu-list is-size-7">
                      <li>
                        <Link
                          to={`${url}/ivr`}
                          className={menuName === "ivr" ? "is-active is-radiusless" : ""}
                          onClick={() => setMenuName("ivr")}
                        >
                          IVR
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${url}/telecare-editor`}
                          className={menuName === "telecare_editor" ? "is-active is-radiusless" : ""}
                          onClick={() => setMenuName("telecare_editor")}
                        >
                          Telecare Editor
                        </Link>
                      </li>
                      <li>
                        <Link
                          to={`${url}/demo-referral-for-dev`}
                          className={menuName === "demo_referral_for_dev" ? "is-active is-radiusless" : ""}
                          onClick={() => setMenuName("demo_referral_for_dev")}
                        >
                          Demo Referral for Dev
                        </Link>
                      </li>
                    </ul>
                  </>
                )}
              </>
            )}
          </aside>
        </div>
        <div className="column is-three-quarters">
          <Switch>
            <Route exact path={path}>
              <Redirect to={`${path}/track-patients`} />
            </Route>
            <Route path={`${path}/track-patients`}>
              <TrackPatients />
            </Route>
            {isPermit(currentUser?.role, SCOPE.OTHER_SETTINGS) && (
              <>
                <Route path={`${path}/clinician-preference`}>
                  <PractitionerSchedule />
                </Route>
                <Route path={`${path}/practitioner-location`}>
                  <PractitionerLocation />
                </Route>
                <Route path={`${path}/speciality`}>
                  <SpecialityEditor />
                </Route>
                <Route path={`${path}/referral-triage-notification`}>
                  <PractitionerEmailReviewingConfig />
                </Route>
                <Route path={`${path}/appointment-booking-notification`}>
                  <PractitionerEmailNotificationConfig />
                </Route>
                <Route path={`${path}/patient-portal-booking-toggle`}>
                  <PractitionerPermissions />
                </Route>
                <Route path={`${path}/sync-coreplus`}>
                  <SyncCoreplus />
                </Route>
                <Route path={`${path}/postcode-timezone-mapping`}>
                  <PostcodeUpload />
                </Route>
                <Route path={`${path}/sms-templates`}>
                  <SMSTemplatesPanel />
                </Route>
                <Route path={`${path}/manual-sms-templates`}>
                  <ManualSmsTemplates />
                </Route>
                <Route path={`${path}/email-templates`}>
                  <EmailTemplates />
                </Route>
                <Route path={`${path}/referrer-communication-preference`}>
                  <ClinicCommunicationMethod />
                </Route>
                <Route path={`${path}/ivr`}>
                  <InteractiveVoiceResponse />
                </Route>
                <Route path={`${path}/email-notification-queue`}>
                  <EmailNotificationQueue />
                </Route>
                <Route path={`${path}/assign-clinician-to-staff`}>
                  <RelationshipPanel />
                </Route>
                <Route path={`${path}/report`}>
                  <Report />
                </Route>
                <Route path={`${path}/account-setting`}>
                  <Coordinators />
                </Route>
                <Route path={`${path}/practitioner-profile`}>
                  <PractitionerProfile />
                </Route>
                {isSecureFilesEnabled && (
                  <Route path={`${path}/secure-file-retention`}>
                    <SecureFileRetentionSettings />
                  </Route>
                )}
                <Route path={`${path}/telecare-editor`}>
                  <TelecareEditorPage />
                </Route>
                <Route path={`${path}/demo-referral-for-dev`}>
                  <CreateDemoReferral />
                </Route>
              </>
            )}
          </Switch>
        </div>
      </div>
    </>
  );
};

export default Settings;
